import * as echarts from 'echarts';
import type { ChartConfig, ChartDesignResolution } from './types';

type ZRenderClip = {
    next: ZRenderClip | null;
    _delay?: number;
    _life?: number;
    _inited: boolean;
    _startTime: number;
    _pausedTime: number;
    _paused: boolean;
    step: (globalTime: number, deltaTime: number) => void;
};

type ZRenderAnimation = {
    _head: ZRenderClip | null;
    stop: () => void;
    start: () => void;
    update: (notify?: boolean) => void;
};

type ZRenderLike = {
    animation: ZRenderAnimation;
    flush: () => void;
};

const NOOP = () => {};

/**
 * Renders a ChartConfig (with colors already resolved by ChartAsset) to the given canvas at the
 * requested scene progress. ECharts' native animations are preserved: the config is loaded with
 * `setOption` once per config identity, and each frame we seek zrender's animation clips
 * directly to `progress × totalDuration` to get a deterministic frame.
 */
export class ChartRenderer {
    #instance: echarts.ECharts | null = null;
    #host: HTMLDivElement | null = null;
    #sourceCanvas: HTMLCanvasElement | null = null;
    #lastConfig: unknown = null;
    #lastWidth = 0;
    #lastHeight = 0;

    renderToCanvas(
        canvas: HTMLCanvasElement,
        config: ChartConfig,
        progress: number,
        destWidth: number = canvas.width,
        destHeight: number = canvas.height,
    ): void {
        const p = Math.max(0, Math.min(1, progress));

        // ECharts is rendered into its own internal canvas sized to the config's design
        // resolution (the canvas size the config was authored against — e.g. 600×600 for
        // built-in defaults), with width adjusted to match the destination region's aspect
        // ratio so percent-based values (grid '14%', pie radius '72%') map cleanly. The
        // result is blitted onto the destination canvas via drawImage, which scales the
        // small source up to fill the target region. Absolute pixel values in the config
        // (fontSize: 28, distance: 10, ...) stay in design units regardless of texture size.
        const { width: ecWidth, height: ecHeight } = computeEChartsCanvasSize(
            destWidth,
            destHeight,
            config.designResolution,
        );

        this.#ensureInstance(ecWidth, ecHeight);

        if (config.echarts !== this.#lastConfig) {
            // First setOption goes onto the fresh instance from `#ensureInstance` above. For
            // every subsequent config change (regenerate-data, color shuffle, prop edit, etc.)
            // dispose + re-init: `setOption(notMerge=true)` alone leaves zombie animators in
            // `zr.animation._head` (we NOOP'd `animation.update` so zrender's finished-clip
            // cleanup never runs), and after a few iterations `seekNativeAnimationsTo` ends up
            // stepping clips whose target display objects have been torn down — at that point
            // the visible animation freezes.
            const isFirstSetOption = this.#lastConfig === null;
            if (!isFirstSetOption) {
                this.#disposeInstance();
                this.#ensureInstance(ecWidth, ecHeight);
            }
            this.#instance!.setOption(config.echarts as echarts.EChartsOption, true);
            this.#lastConfig = config.echarts;
            // setOption can revive zrender's animation loop; re-pin the NOOPs.
            this.#neutralizeAnimationLoop();
            // Grab echarts' internal canvas on first draw so we can blit from it each frame.
            this.#sourceCanvas = this.#findZrCanvas();
        }

        this.#seekNativeAnimationsTo(p);

        if (this.#sourceCanvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(this.#sourceCanvas, 0, 0, destWidth, destHeight);
            }
        }
    }

    #ensureInstance(width: number, height: number): void {
        if (this.#instance && this.#host) {
            if (this.#lastWidth === width && this.#lastHeight === height) {
                return;
            }
            this.#disposeInstance();
        }
        this.#host = document.createElement('div');
        this.#host.style.width = width + 'px';
        this.#host.style.height = height + 'px';
        this.#instance = echarts.init(this.#host, undefined, {
            renderer: 'canvas',
            width,
            height,
        });
        this.#neutralizeAnimationLoop();
        this.#lastWidth = width;
        this.#lastHeight = height;
        this.#lastConfig = null;
    }

    #findZrCanvas(): HTMLCanvasElement | null {
        if (!this.#host) return null;
        return this.#host.querySelector('canvas');
    }

    /**
     * Luminary drives chart progress manually, so zrender's rAF loop must never tick: the
     * first frame it processes would race real wall-clock time against each clip's `_startTime`
     * and push short-lived animations (bar growth, line reveal) straight to their final frame
     * before we ever call `seek`. We stub the loop at init time — before `setOption` can
     * schedule any animations — so clips stay frozen at their creation state until we advance
     * them explicitly via `#seekNativeAnimationsTo`.
     */
    #neutralizeAnimationLoop(): void {
        const animation = this.#getZr().animation;
        animation.stop();
        animation.start = NOOP;
        animation.update = NOOP;
    }

    #getZr(): ZRenderLike {
        return this.#instance!.getZr() as unknown as ZRenderLike;
    }

    #disposeInstance(): void {
        if (this.#instance) {
            this.#instance.dispose();
            this.#instance = null;
        }
        this.#host = null;
        this.#sourceCanvas = null;
        this.#lastConfig = null;
        this.#lastWidth = 0;
        this.#lastHeight = 0;
    }

    destroy(): void {
        this.#disposeInstance();
    }

    #seekNativeAnimationsTo(progress: number): void {
        const zr = this.#getZr();

        const clips: ZRenderClip[] = [];
        for (let c = zr.animation._head; c; c = c.next) clips.push(c);
        if (clips.length === 0) {
            zr.flush();
            return;
        }

        let totalDuration = 0;
        for (const c of clips) {
            totalDuration = Math.max(totalDuration, (c._delay || 0) + (c._life || 0));
        }
        const globalTime = progress * totalDuration;

        for (const c of clips) {
            c._inited = true;
            c._startTime = c._delay || 0;
            c._pausedTime = 0;
            c._paused = false;
            c.step(globalTime, 0);
        }
        zr.flush();
    }
}

export function computeEChartsCanvasSize(
    destWidth: number,
    destHeight: number,
    designResolution: ChartDesignResolution | undefined,
): { width: number; height: number } {
    if (
        !designResolution ||
        designResolution.width <= 0 ||
        designResolution.height <= 0 ||
        destWidth <= 0 ||
        destHeight <= 0
    ) {
        // Legacy path (no designResolution): render at the destination canvas's pixel size.
        // fontSize/lineWidth/etc are interpreted at the texture resolution.
        return { width: destWidth, height: destHeight };
    }
    // Anchor the echarts canvas's smaller dimension to the design resolution's smaller
    // dimension; derive the longer dimension from the destination aspect ratio. This keeps
    // absolute pixel values (fontSize, lineWidth, ...) in lockstep with percent values that
    // are resolved against min(canvasW, canvasH) (e.g. pie `radius: '72%'`), so text and the
    // pie scale together when the asset is resized vertically or horizontally.
    const designMinDim = Math.min(designResolution.width, designResolution.height);
    if (destWidth >= destHeight) {
        return { width: (designMinDim * destWidth) / destHeight, height: designMinDim };
    }
    return { width: designMinDim, height: (designMinDim * destHeight) / destWidth };
}
